import { sameWordFamily, stemTopicToken } from './topic-supporting-pages';

/**
 * "Add X" when our page already has X — and the line that proves it (TECHNICAL_DEBT §0cb, section
 * half; §0ce, new-page ideas).
 *
 * The section comparison works section to section, by similarity, and the rival's heading names a
 * topic our page already gives a section of its own: a rival's "How to write a <thing>" against our
 * H2 of the same words, scored under the cut-off because the bodies differ. Rounds of similarity
 * tests could not separate those from real gaps (sprint 4 measured about 10% right corpus-wide).
 * This reads our page's own stored text instead, and withholds an item only when it can QUOTE where
 * the page already covers it. /review shows nothing for it (owner, 2026-10-19: "if there's no
 * issue, we shouldn't talk about issues"); the quote goes to the everything-mode export only, so a
 * grader or an audit can check the claim.
 *
 * The test is deliberately narrow, a passage that is ABOUT the rival's heading, not one that
 * mentions it:
 * - every content word of the heading is in the passage (same word family: "secure"/"security"),
 * - and those words are most of the passage (`ALREADY_ON_PAGE_MIN_PASSAGE_SHARE`), so a sentence
 *   that names a term in passing does not answer "What causes <term>?",
 * - and both ask the same thing: a "Why…" heading is not answered by a "How to…" one, a statement
 *   is not answered by a question, and "No <feature>" (from a list of cons) is not a section on
 *   the feature.
 *
 * Passages are our headings (H2 and below for a section; the H1 names the whole page) and the
 * sentences and table rows of the stored main text — `main_topic` and `secondary_topic`, where the
 * crawler files a tool page's benefits and a template page's tables. Text that is only a link (a
 * related-post card, the nav) is not the page covering anything.
 *
 * No model, no embeddings: words in our stored page, nothing else.
 */

/**
 * A heading needs this many content words before a passage can be said to cover it.
 *
 * One word ("Availability", "Pricing") is a subject, not a section: a heading of ours that shares
 * it says nothing about what the rival's section does with it. On both sites' stored comparisons
 * (2026-10-19 data, 511 + 613 gap rows) a single-word heading was 1 of the graded good items
 * and none of the graded "already on the page" ones.
 */
export const ALREADY_ON_PAGE_MIN_LABEL_WORDS = 2;

/**
 * The share of a passage's own content words that must belong to the heading.
 *
 * Measured on every stored rival comparison of both sites (2026-10-19 data): at 0.6 the rule fires
 * on 6 of 511 ManyRequests gap rows and 4 of 613 SlideSpeak rows, 9 of the 10 right when read
 * against the stored page (the wrong one: a two-word heading on a legal page against a boilerplate
 * sentence that happens to use both words), and on none of the graded good items. The loosest
 * passage a graded "already on the page" item needs is 2 of 3 words (a short benefit line on a tool
 * page against a two-word rival heading: 0.67); 0.5 adds two more rows, both right, and nothing
 * wrong; 0.67 loses the graded one. 0.6 sits between them.
 */
export const ALREADY_ON_PAGE_MIN_PASSAGE_SHARE = 0.6;

// Words that carry no subject. Question words are NOT dropped from the comparison of what a heading
// asks (`askOf`); they are only left out of the content words.
const STOP_WORDS = new Set(
  `a an the and or for to of in on at by is are was were be been being with from as it its your you
yours our ours we us what how why when where who which whom vs best top this that these those can could should would
will do does did done not no yes if then than so such into about over under more most less very just also any all
each every some own other new get got make made use used using via per like up out off their them they his her
my me i here there have has had having one two three four five six seven eight nine ten first second third
step steps way ways`.split(/\s+/)
);

const QUESTION_WORDS = new Set(['how', 'why', 'what', 'where', 'when', 'who', 'which', 'should', 'can', 'does', 'do', 'is', 'are', 'will']);
const NEGATIONS = new Set(['no', 'not', 'without', 'never', 'none', 'cannot']);

/**
 * "What does a <thing> include?" is answered by "Typically, a <thing> contains:" and a list. The
 * verb differs, so these questions and passages are compared on the thing whose parts they list,
 * with the verb left out. One graded item has this shape; the rule fires on no other stored row.
 */
const PARTS_QUESTION = /^\s*(what\s+(does|do|should)\s+.+\s+(include|contain|have)\b|what\s+to\s+include\b|what\s+(should|goes)\s+(be\s+)?in(to)?\b)/i;
const PARTS_PASSAGE = /\b(includes?|including|contains?|consists?\s+of|what\s+to\s+include|elements|components)\b/i;
const PARTS_WORDS = ['include', 'contain', 'consist', 'element', 'component', 'have', 'should'].map(stemTopicToken);

const SENTENCE_BREAK = /(?<=[.!?])\s+(?=[A-Z0-9"“(])|\n+/;

export interface OwnPagePassage {
  kind: 'heading' | 'text';
  /** Heading level for a heading; the level of the section it sits in for text. Null when unknown. */
  level: number | null;
  /** The heading the passage sits under (itself, for a heading). */
  section: string;
  text: string;
}

/** Where our page already covers a rival's section, in our own words. */
export interface AlreadyOnPageQuote {
  /** "H2" for a heading of ours, "text" for a sentence or table row. */
  where: string;
  /** The heading the quote sits under ('' when none). */
  section: string;
  /** Our words, as stored. */
  text: string;
}

function words(text: string): string[] {
  return (text || '')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
}

function contentStems(text: string): string[] {
  const out: string[] = [];
  for (const w of words(text)) {
    if (w.length < 3 || STOP_WORDS.has(w) || /^\d+$/.test(w)) continue;
    const s = stemTopicToken(w);
    if (!out.includes(s)) out.push(s);
  }
  return out;
}

function hasFamily(pool: readonly string[], stem: string): boolean {
  return pool.some((p) => sameWordFamily(p, stem));
}

/** What a heading asks: its question word ('what is' for a definition), 'parts', or '' for a statement. */
function askOf(text: string): string {
  if (PARTS_QUESTION.test(text || '')) return 'parts';
  let w = words(text);
  while (w.length && (/^\d+$/.test(w[0]) || w[0] === 'step')) w = w.slice(1);
  if (!w.length || !QUESTION_WORDS.has(w[0])) return '';
  if (w[0] === 'what' && (w[1] === 'is' || w[1] === 'are' || w[1] === 's')) return 'what is';
  return w[0];
}

function negated(text: string): boolean {
  return words(text).some((w) => NEGATIONS.has(w));
}

function blockList(value: unknown): any[] {
  if (Array.isArray(value)) return value;
  return value && typeof value === 'object' ? [value] : [];
}

/**
 * Our page's headings and sentences, from the stored analysis (`rawData.structuredContent`), in
 * page order. Empty when nothing is stored.
 */
export function ownPagePassages(analysis: any): OwnPagePassage[] {
  const sc = analysis?.rawData?.structuredContent;
  if (!sc || typeof sc !== 'object') return [];
  const out: OwnPagePassage[] = [];
  for (const part of ['main_topic', 'secondary_topic']) {
    for (const block of blockList(sc[part])) {
      const heading = String(block?.h_title ?? '').replace(/\s+/g, ' ').trim();
      const level = typeof block?.level === 'number' ? block.level : null;
      if (heading) out.push({ kind: 'heading', level, section: heading, text: heading });
      const texts: string[] = [];
      for (const key of ['primary_content', 'secondary_content']) {
        for (const item of blockList(block?.[key])) {
          if (item?.url) continue;
          if (typeof item?.text === 'string') texts.push(item.text);
        }
      }
      for (const table of blockList(block?.table_content)) {
        for (const key of ['header', 'body', 'footer']) {
          for (const row of blockList(table?.[key])) {
            texts.push(blockList(row?.row_cells).map((c) => String(c?.text ?? '')).join(' | '));
          }
        }
      }
      for (const text of texts) {
        for (const sentence of text.split(SENTENCE_BREAK)) {
          const s = sentence.replace(/\s+/g, ' ').trim();
          if (s.length >= 3) out.push({ kind: 'text', level, section: heading, text: s });
        }
      }
    }
  }
  return out;
}

/**
 * The passage of ours that is about `heading`, or null. Headings are tried before text, so the
 * quote is the most checkable line there is.
 *
 * The H1 counts only with `pageTitle`: it names the whole page, so it answers "do we have a page on
 * X" (a new-page idea) and not "do we have a section on X" (a rival's section).
 */
export function alreadyOnPageQuote(
  heading: string,
  passages: readonly OwnPagePassage[],
  opts: { pageTitle?: boolean } = {}
): AlreadyOnPageQuote | null {
  const parts = askOf(heading) === 'parts';
  const drop = (stems: string[]) => (parts ? stems.filter((s) => !hasFamily(PARTS_WORDS, s)) : stems);
  const target = drop(contentStems(heading));
  if (target.length < ALREADY_ON_PAGE_MIN_LABEL_WORDS) return null;
  const ask = askOf(heading);
  const neg = negated(heading);

  const headings = passages.filter((p) => p.kind === 'heading' && (p.level !== 1 || opts.pageTitle));
  const ordered = [...headings.filter((p) => p.level === 1), ...headings.filter((p) => p.level !== 1), ...passages.filter((p) => p.kind === 'text')];
  for (const p of ordered) {
    if (parts ? !PARTS_PASSAGE.test(p.text) : askOf(p.text) !== ask) continue;
    if (negated(p.text) !== neg) continue;
    const pool = drop(contentStems(p.text));
    if (!pool.length || !target.every((t) => hasFamily(pool, t))) continue;
    const share = pool.filter((s) => hasFamily(target, s)).length / pool.length;
    if (share < ALREADY_ON_PAGE_MIN_PASSAGE_SHARE) continue;
    return {
      where: p.kind === 'heading' ? (p.level ? `H${p.level}` : 'heading') : 'text',
      section: p.kind === 'heading' ? '' : p.section,
      text: p.text
    };
  }
  return null;
}

/**
 * The subject a new-page idea names, from the rival page's final path segment, without a trailing
 * "in …" / "for …": "<term>-in-project-management" is a page on the term, and our page that already
 * defines the term under its own heading does not repeat the context words there.
 */
export function newPageIdeaSubject(url: string): string {
  let path = url;
  try {
    path = new URL(url).pathname || '/';
  } catch {
    // Not absolute: read it as a path.
  }
  const segments = path.split('/').map((s) => s.trim()).filter(Boolean);
  const words = (segments[segments.length - 1] || '').replace(/[-_]+/g, ' ').trim();
  return words.split(/\s+(?:in|for)\s+/i)[0].trim();
}

/** How a quote reads in one line: `your H2 “…”`, or `your text under “…”: “…”`. */
export function alreadyOnPageQuoteLine(q: AlreadyOnPageQuote): string {
  if (q.where !== 'text') return `your ${q.where} “${q.text}”`;
  return q.section ? `your text under “${q.section}”: “${q.text}”` : `your text: “${q.text}”`;
}
